import React, { useEffect, useMemo, useRef, useState } from "react";
import { CheckIcon, MailIcon } from "lucide-react";
import { scrollToElement } from "../../utils/scroll";
import { trackOnce } from "../../utils/tracking";
import { CALENDAR_EMBED_SCRIPT, CALENDAR_URL, type Contact } from "./useApplicationWizard";
type WizardResultProps = {
  qualified: boolean;
  contact: Contact;
};
export function WizardResult({
  qualified,
  contact
}: WizardResultProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!rootRef.current) return;
    // Account for the sticky wizard header on mobile.
    const extra = window.matchMedia("(max-width: 767px)").matches ? 56 : 0;
    scrollToElement(rootRef.current, extra);
  }, []);
  return <div ref={rootRef} role="status" data-id="element-69" data-mp="1r5uskn4c75cl">
      {qualified ? <QualifiedScreen contact={contact} data-id="element-70" data-mp="3qh5pfc1cdcc4" /> : <NotQualifiedScreen data-id="element-71" data-mp="3qg2tgpp5wjpc" />}
    </div>;
}
function QualifiedScreen({
  contact
}: {
  contact: Contact;
}) {
  const [loaded, setLoaded] = useState(false);
  const bookingUrl = useMemo(() => buildBookingUrl(contact), [contact]);
  useEffect(() => {
    trackOnce("QualifiedViewCalendar", {}, {
      custom: true
    });
    if (document.querySelector(`script[src="${CALENDAR_EMBED_SCRIPT}"]`)) return;
    const script = document.createElement("script");
    script.src = CALENDAR_EMBED_SCRIPT;
    script.type = "text/javascript";
    script.async = true;
    document.body.appendChild(script);
  }, []);
  return <div data-id="element-72" data-mp="1fpq99uuzlb24">
      <span className="flex h-10 w-10 items-center justify-center rounded-[3px] border border-gold" data-id="element-73" data-mp="1frb7bugifc4q">
        <CheckIcon className="h-5 w-5 text-gold" strokeWidth={2.5} aria-hidden="true" data-id="element-74" data-mp="1kqe0agj7trwc" />
      </span>
      <h3 className="mt-8 font-display text-[28px] uppercase leading-[1.05] tracking-[-0.01em] text-cream sm:text-[36px]" data-id="element-75" data-mp="15xasv1zc8ujc">
        <span className="text-gold" data-id="element-76" data-mp="1yo2jzl1jqszt">¡Calificas!</span> Elige el horario de tu diagnóstico (15 min)
      </h3>

      <div className="relative mt-8 min-h-[720px] md:min-h-[640px]" data-id="element-77" data-mp="23ej39ls1t8u3">
        {!loaded && <div className="absolute inset-0 overflow-hidden rounded-[3px] border border-line bg-surface" aria-hidden="true" data-id="element-78" data-mp="2daj51qk11ssw">
            <div className="absolute inset-x-0 top-0 h-px animate-pulse bg-gold/60" data-id="element-79" data-mp="28chmd6qijvhy" />
            <div className="space-y-4 p-6" data-id="element-80" data-mp="23b0nthvi4l4j">
              <div className="h-4 w-1/3 animate-pulse rounded-[2px] bg-white/[0.06]" data-id="element-81" data-mp="1zb087two23vw" />
              <div className="h-px w-full bg-line" data-id="element-82" data-mp="1zdco81iv646b" />
              <div className="grid grid-cols-7 gap-2" data-id="element-83" data-mp="1ucp1hs1o49hw">
                {Array.from({
              length: 28
            }).map((_, i) => <div key={i} className="aspect-square animate-pulse rounded-[2px] bg-white/[0.04]" data-id="element-84" data-mp="1pjm2h4e4g7ub" data-mpi={i} />)}
              </div>
            </div>
          </div>}
        <iframe src={bookingUrl} title="Agenda tu diagnóstico — Studio Web VIP" allow="payment" scrolling="no" onLoad={() => setLoaded(true)} className={`relative min-h-[720px] md:min-h-[640px] ${loaded ? "opacity-100" : "opacity-0"}`} style={{
        width: "100%",
        border: "none",
        overflow: "hidden"
      }} data-id="element-85" data-mp="1foc2em7aaaj6" />
      </div>

      <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex min-h-[48px] items-center text-[14px] text-muted underline-offset-4 transition-colors duration-200 ease-out hover:text-cream hover:underline" data-id="element-86" data-mp="3n7pzziia55up">
        ¿No carga el calendario? Ábrelo aquí →
      </a>
    </div>;
}
function NotQualifiedScreen() {
  return <div data-id="element-87" data-mp="33k0c3mdgrpqr">
      <span className="flex h-10 w-10 items-center justify-center rounded-[3px] border border-steel" data-id="element-88" data-mp="33lla5lyzlqtd">
        <MailIcon className="h-5 w-5 text-gold" aria-hidden="true" data-id="element-89" data-mp="38j3a4mv10vp8" />
      </span>
      <h3 className="mt-8 font-display text-[26px] uppercase leading-[1.05] tracking-[-0.01em] text-cream sm:text-[30px]" data-id="element-90" data-mp="3dauzwv6cyxeb">
        Gracias por tu interés.
      </h3>
      <p className="mt-4 max-w-lg text-muted" data-id="element-91" data-mp="2kuak17135i2j">
        Por ahora este programa está diseñado para negocios con más volumen. Te
        enviaremos recursos útiles a tu email.
      </p>
    </div>;
}
function buildBookingUrl(contact: Contact): string {
  const [firstName = "", ...rest] = contact.name.trim().split(/\s+/);
  const params = new URLSearchParams({
    first_name: firstName,
    last_name: rest.join(" "),
    email: contact.email.trim(),
    phone: contact.phone.trim()
  });
  return `${CALENDAR_URL}?${params.toString()}`;
}